import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './MockExamRegistration.css';

import { Link, LoadingScroll, Snackbar } from 'geniebook-ui/dist';
import { IUserData } from '@models/interfaces/user.interface';
import {
  IScheduleListRes,
  IStudentListRes,
  MockExamParent,
  MockExamStudent,
  Schedule,
} from '@models/interfaces/mock_exam.interface';
import { ErrorObject } from '@models/interfaces/global.interface';
import ArrowLeft from '@assets/icons/arrow-left';

import AuthLayout from '@layouts/AuthLayout';
import ParentRegistrationForm from '@components/mock_exam/ParentRegistrationForm';
import LoginForm from '@components/mock_exam/LoginForm';
import StudentForm from '@components/mock_exam/StudentForm';
import BookSession from '@components/mock_exam/BookSession';
import ThankYouPage from '@components/mock_exam/ThankYouPage';
import mockExamService from '@services/MockExamServices.service';

import {
  MOCK_EXAM_LEVEL_OPTIONS,
  MOCK_EXAM_PARENT_DATA_KEY,
  MOCK_EXAM_STEP_KEY,
  MOCK_EXAM_STUDENTS_DATA_KEY,
  MOCK_EXAM_UID_KEY,
  PAGE_ADD_STUDENT,
  PAGE_BOOK_SESSIONS,
  PAGE_LOGIN_PARENT,
  PAGE_MOCK_EXAM_CLOSED,
  PAGE_MOCK_EXAM_FORGET,
  PAGE_REGISTER_PARENT,
  PAGE_THANK_YOU,
} from '@utils/constants/mock-exam.constants';
import Cookies from 'js-cookie';
import { AxiosResponse } from 'axios';
import Preloading from '@components/Preloading';
import MockExamBackground from '@assets/illustration/MockExamBackground';
import MockExamClosed from '@components/mock_exam/MockExamClosed';
import ForgotPasswordCard from '@components/auth/ForgotPasswordCard';
import moment from 'moment';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
const title_mapping = {
  '1.1': 'Enter your details',
  '1.2': 'Log in with parent account',
  '1.3': 'Enter your details',
  '2': "Enter child's details",
  '3': 'Select children to enrol',
  '4': 'Schedule mock exams',
  '5': 'Thank you for registering!',
  '6': 'Registration is now closed',
};
interface TitleMapping {
  '1.1': string;
  '1.2': string;
  '1.3': string;
  '2': string;
  '3': string;
  '4': string;
  '5': string;
  '6': string;
}

const MockExamRegistration = () => {
  const mockExamLastDay = new Date(2023, 7, 31, 21, 0, 0); // 31 August 9PM GMT+8
  const nowMoment = moment(new Date()); // Now Time in local Timezone

  // for detecting timezone offset https://stackoverflow.com/a/8090836
  const timezone = parseInt(
    (-(new Date().getTimezoneOffset() / 60)).toString(),
  );
  nowMoment.hour(nowMoment.hour() - timezone + 8); // Now Time in GMT+8
  const isMockExamClosed: boolean =
    nowMoment.toDate().getTime() > mockExamLastDay.getTime();

  const firstStep = PAGE_LOGIN_PARENT;
  const [step, setStep] = useState<string>(
    isMockExamClosed
      ? PAGE_MOCK_EXAM_CLOSED
      : localStorage.getItem(MOCK_EXAM_STEP_KEY) !== null
      ? localStorage.getItem(MOCK_EXAM_STEP_KEY)!
      : (Cookies.get(MOCK_EXAM_UID_KEY) || null) === null
      ? firstStep
      : PAGE_ADD_STUDENT,
  );

  // too make sure snack bar placed correctly
  useEffect(() => {
    window.addEventListener('resize', () => setViewWidth(window.innerWidth));
    setViewWidth(window.innerWidth);
    return () => {
      window.removeEventListener('resize', () =>
        setViewWidth(window.innerWidth),
      );
    };
  }, []);

  const [isExistingParent, setIsExistingParent] = useState<boolean>(
    (Cookies.get(MOCK_EXAM_UID_KEY) || null) !== null,
  );

  const [regParentData, setRegParentData] = useState<MockExamParent>(
    !isMockExamClosed &&
      localStorage.getItem(MOCK_EXAM_PARENT_DATA_KEY) !== null
      ? JSON.parse(localStorage.getItem(MOCK_EXAM_PARENT_DATA_KEY)!)
      : {
          first_name: '',
          last_name: '',
          email: '',
          country_code: '',
          mobile: '',
        },
  );

  const [students, setStudents] = useState<MockExamStudent[]>(
    !isMockExamClosed &&
      localStorage.getItem(MOCK_EXAM_STUDENTS_DATA_KEY) !== null
      ? JSON.parse(localStorage.getItem(MOCK_EXAM_STUDENTS_DATA_KEY)!)
      : [],
  );
  const [bookSessionEmailSentTo, setBookSessionEmailSentTo] =
    useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorObject>({
    error: false,
    error_msg: '',
    error_code: '',
  });
  const [viewWidth, setViewWidth] = useState<number>(0);

  const setTitle = (step: string) => {
    return title_mapping[step as keyof TitleMapping];
  };

  const handleBackStep = () => {
    switch (step) {
      case PAGE_ADD_STUDENT:
        if (!isExistingParent) setStep(PAGE_REGISTER_PARENT);
        else {
          setStep(PAGE_LOGIN_PARENT);
          handleAccountLogout();
        }
        break;
      case PAGE_BOOK_SESSIONS:
        redirectToStudentList();
        break;
      default:
        setStep(firstStep);
        break;
    }
  };

  // login states and handler
  const [scheduleList, setScheduleList] = useState<Schedule[] | null>(null);
  const [isLoadingOnLoad, setisLoadingOnLoad] = useState<boolean>(true);

  const handleAccountLogin = (
    data: IUserData[],
    options: { remember_me: boolean },
  ) => {
    localStorage.setItem('multipleAccountsLogin', JSON.stringify(data));
    localStorage.setItem(
      'rememberMeLogin',
      JSON.stringify(options.remember_me),
    );

    localStorage.removeItem(MOCK_EXAM_STEP_KEY);
    localStorage.removeItem(MOCK_EXAM_PARENT_DATA_KEY);
    localStorage.removeItem(MOCK_EXAM_STUDENTS_DATA_KEY);
  };

  const handleAccountLogout = () => {
    Cookies.remove(MOCK_EXAM_UID_KEY);
    Cookies.remove('user_type');
    Cookies.remove('is_login');

    setIsExistingParent(false);
    setRegParentData({
      first_name: '',
      last_name: '',
      email: '',
      country_code: '',
      mobile: '',
    });
    setStudents([]);
    localStorage.removeItem(MOCK_EXAM_STEP_KEY);
    localStorage.removeItem(MOCK_EXAM_PARENT_DATA_KEY);
    localStorage.removeItem(MOCK_EXAM_STUDENTS_DATA_KEY);
  };

  useEffect(() => {
    if (error.error && error.error_msg !== '') {
      setTimeout(() => {
        setError({
          error: false,
          error_msg: '',
          error_code: '',
        });
      }, 5000);
    }
  }, [error]);

  useEffect(() => {
    if (isLoadingOnLoad && step === PAGE_ADD_STUDENT) {
      redirectToStudentList();
    } else if (isLoadingOnLoad && step === PAGE_BOOK_SESSIONS) {
      redirectToBookSession();
    } else {
      setisLoadingOnLoad(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoadingOnLoad) {
      if (
        step !== PAGE_THANK_YOU &&
        step !== PAGE_LOGIN_PARENT &&
        step !== PAGE_REGISTER_PARENT &&
        step !== PAGE_MOCK_EXAM_FORGET &&
        step !== PAGE_MOCK_EXAM_CLOSED
      ) {
        localStorage.setItem(MOCK_EXAM_STEP_KEY, step);

        localStorage.setItem(
          MOCK_EXAM_PARENT_DATA_KEY,
          JSON.stringify(regParentData),
        );

        localStorage.setItem(
          MOCK_EXAM_STUDENTS_DATA_KEY,
          JSON.stringify(students),
        );
      } else {
        localStorage.removeItem(MOCK_EXAM_STEP_KEY);
        localStorage.removeItem(MOCK_EXAM_PARENT_DATA_KEY);
        localStorage.removeItem(MOCK_EXAM_STUDENTS_DATA_KEY);
      }
    }
  }, [step, students, regParentData]);

  const redirectToBookSession = () => {
    setLoading(true);
    setTimeout(async () => {
      try {
        const res: AxiosResponse = await mockExamService.getScheduleList();
        const body: IScheduleListRes = res.data;
        if (body.error) {
          setError({
            error: body.error,
            error_msg: body.error_msg,
            error_code: body.error_code,
          });
        } else {
          setScheduleList(body.data);
          setStep(PAGE_BOOK_SESSIONS);
        }
      } catch (errorCatch) {
        setError({
          error: true,
          error_msg: 'Unknown Error',
          error_code: '',
        });
      } finally {
        setLoading(false);
        setisLoadingOnLoad(false);
      }
    }, 100);
  };

  const redirectToStudentList = (fetchData?: boolean) => {
    setLoading(true);
    setTimeout(async () => {
      try {
        if ((fetchData !== undefined && fetchData) || students.length == 0) {
          const res: AxiosResponse = await mockExamService.getStudentList();
          const body: IStudentListRes = res.data;
          if (body.error) {
            setError({
              error: body.error,
              error_msg: body.error_msg,
              error_code: body.error_code,
            });
          } else {
            const newStudents: MockExamStudent[] = body.data;
            if (newStudents.length === 0) {
              newStudents.push({
                student_id: null,
                first_name: '',
                last_name: '',
                student_name: '',
                level_id: parseInt(MOCK_EXAM_LEVEL_OPTIONS[0].value + ''),
                level_name: MOCK_EXAM_LEVEL_OPTIONS[0].label,
                classes: [],
                savedClasses: [],
                checked: true,
                levelPopupOpen: false,
                fullNameError: '',
                levelError: '',
              });
            } else {
              for (let i = 0; i < newStudents.length; i++) {
                newStudents[i].savedClasses = newStudents[i].classes;
                newStudents[i].levelPopupOpen = false;
                newStudents[i].fullNameError = '';
                newStudents[i].levelError = '';
              }
            }
            setStudents(newStudents);
            setStep(PAGE_ADD_STUDENT);
          }
        } else {
          setStep(PAGE_ADD_STUDENT);
        }
      } catch (errorCatch) {
        setError({
          error: true,
          error_msg: 'Unknown Error',
          error_code: '',
        });
      } finally {
        setLoading(false);
        setisLoadingOnLoad(false);
      }
    }, 100);
  };

  useEffect(() => {
    mockExamService.visitTracker(true, step);
  }, [step]);

  return (
    <>
      <Helmet>
        <title>Geniebook</title>
        <meta name="description" content="Geniebook" />
      </Helmet>
      <AuthLayout
        illustration=""
        className="mock-exam-layout"
        animationContent={
          <div className="mock-exam-left-container">
            <div className="mock-exam-left-content"></div>
          </div>
        }
      >
        <>
          <div className="w-full">
            <div className="container-form">
              {step != PAGE_THANK_YOU &&
                step !== PAGE_LOGIN_PARENT &&
                step !== PAGE_REGISTER_PARENT &&
                step !== PAGE_MOCK_EXAM_CLOSED && (
                  <div
                    onClick={handleBackStep}
                    className="text-primary-apricot font-bold mb-4 underline flex flex-row item-center gap-1 hover:text-primary-skyblue hover:cursor-pointer"
                  >
                    <div className="pt-[6px]">
                      <ArrowLeft className="w-[14px] h-4 inline-block fill-current" />
                    </div>
                    <div>Back</div>
                  </div>
                )}
              <div className="font-black text-primary-navyblue text-xl leading-[32px] mb-4">
                {setTitle(step)}
              </div>
              {step == PAGE_REGISTER_PARENT && (
                <ParentRegistrationForm
                  regParentData={regParentData}
                  setRegParentData={setRegParentData}
                  setError={setError}
                  setStep={setStep}
                  redirectToStudentList={redirectToStudentList}
                />
              )}
              {step == PAGE_LOGIN_PARENT && (
                <>
                  <LoginForm
                    selectAccountState={handleAccountLogin}
                    setStep={setStep}
                    setIsExistingParent={setIsExistingParent}
                    redirectToStudentList={redirectToStudentList}
                  />
                  <div className="mt-7 flex justify-center">
                    <div
                      onClick={() => {
                        setStep(PAGE_MOCK_EXAM_FORGET);
                      }}
                    >
                      <Link className="text-[16px] !font-bold cursor-pointer">
                        Forgot your password?
                      </Link>
                    </div>
                  </div>
                </>
              )}
              {step == PAGE_MOCK_EXAM_FORGET && (
                <ForgotPasswordCard
                  backToLoginCallback={() => setStep(PAGE_LOGIN_PARENT)}
                />
              )}
              {step == PAGE_ADD_STUDENT && (
                <StudentForm
                  students={students}
                  setStudents={setStudents}
                  loading={loading}
                  setLoading={setLoading}
                  redirectToBookSession={redirectToBookSession}
                  isExistingParent={isExistingParent}
                />
              )}
              {step == PAGE_BOOK_SESSIONS && (
                <BookSession
                  students={students}
                  setStudents={setStudents}
                  regParentData={regParentData}
                  setBookSessionEmailSentTo={setBookSessionEmailSentTo}
                  loading={loading}
                  setLoading={setLoading}
                  setError={setError}
                  setStep={setStep}
                  scheduleList={scheduleList}
                />
              )}
              {step == PAGE_THANK_YOU && (
                <ThankYouPage parentEmail={bookSessionEmailSentTo} />
              )}
              {step == PAGE_MOCK_EXAM_CLOSED && <MockExamClosed />}
            </div>
          </div>
          <MockExamBackground className="mock-exam-background" />
        </>
      </AuthLayout>
      {loading && <LoadingScroll isBlockOthers={true} />}
      {error.error_msg !== '' && (
        <Snackbar
          message={error.error_msg}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() =>
            setError({
              error: false,
              error_msg: '',
              error_code: '',
            })
          }
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      )}
      {isLoadingOnLoad && <Preloading className="z-10" />}
    </>
  );
};

export default MockExamRegistration;
